import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { SecureContent } from '../utils/sanitize';
import { getImageUrlForStyle, getImageUrl } from '../utils/imageUtils';

const SecretaryMessagePage = () => {
  const [secretaryData, setSecretaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchSecretaryData = async () => {
      try {
        setLoading(true);
        const response = await apiService.getSecretaryMessage();
        setSecretaryData(response.data);
      } catch (error) {
        setError('Failed to load secretary message');
        console.error('Error fetching secretary message:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSecretaryData();
  }, []);

  if (loading) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-crimson mb-4"></i>
          <p className="text-gray-600">Loading Secretary's Message...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-exclamation-circle text-4xl text-red-500 mb-4"></i>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Hero Banner */}
      <div className="relative bg-slate py-20">
        {secretaryData?.bannerImage && (
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: getImageUrlForStyle(secretaryData.bannerImage) }}
          ></div>
        )}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-sans font-bold text-white mb-4">
            {secretaryData?.bannerTitle || "Secretary's Message"}
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            {secretaryData?.bannerSubtitle || 'Committed to operational excellence and institutional growth'}
          </p>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          {/* Main Content Card */}
          <div className="bg-white rounded-xl shadow-soft p-12 md:p-16">
            <div className="grid md:grid-cols-4 gap-12 mb-8">
              {/* Photo */}
              <div className="md:col-span-1">
                <div className="bg-gray-100 rounded-lg aspect-[3/4] flex items-center justify-center overflow-hidden">
                  {secretaryData?.secretaryImage ? (
                    <img 
                      src={getImageUrl(secretaryData.secretaryImage)} 
                      alt={secretaryData.secretaryName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <i className="fas fa-user text-8xl text-gray-400"></i>
                  )}
                </div>
                <div className="mt-6 text-center">
                  <h3 className="font-bold text-xl text-slate">
                    {secretaryData?.secretaryName || 'Ms. Priya Sharma'}
                  </h3>
                  <p className="text-base text-gray-500 mt-2">
                    {secretaryData?.secretaryTitle || 'Secretary'}
                  </p>
                  <p className="text-sm text-gray-400 mt-1">
                    {secretaryData?.secretaryInstitution || 'FiT Engineering College'}
                  </p>
                </div>
              </div>
            
              {/* Message Content */}
              <div className="md:col-span-3 space-y-6">
                <SecureContent 
                  content={secretaryData?.messageContent || 
                    `<p>Dear Members of the FIT Community,</p>
                     <p>It gives me immense pleasure to serve as Secretary of FiT Engineering College...</p>`
                  }
                  className="text-gray-600 leading-relaxed rich-text-content"
                />
                <p className="text-gray-600 leading-relaxed font-semibold mt-8">
                  Best wishes,<br/>
                  <span className="text-crimson text-xl">{secretaryData?.secretaryName || 'Ms. Priya Sharma'}</span><br/>
                  <span className="text-base font-normal">
                    {secretaryData?.secretaryTitle || 'Secretary'}, {secretaryData?.secretaryInstitution || 'FiT Engineering College'}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecretaryMessagePage;